/**
 * Parser de errores para convertir mensajes tecnicos en mensajes amigables
 */

// Colores para resaltar valores
const typeColors = {
  duplicate: '#d95565',
  validation: '#F0AA61',
  network: '#73B0F4',
  error: '#d95565',
};

// Mapeo de campos a nombres legibles
const fieldNames = {
  identification: 'cédula',
  driver_identification: 'cédula del conductor',
  client_identification: 'cédula del cliente',
  email: 'correo electrónico',
  phone: 'teléfono',
  plate_number: 'número de placa',
  license_plate: 'número de placa',
  name: 'nombre',
  surname: 'apellido',
  ruc: 'RUC',
  username: 'nombre de usuario',
  period_name: 'nombre del periodo',
};

// Mapeo de tablas a nombres legibles
const tableNames = {
  driver: 'conductor',
  drivers: 'conductores',
  vehicle: 'vehiculo',
  vehicles: 'vehiculos',
  user: 'usuario',
  users: 'usuarios',
  company: 'empresa',
  companies: 'empresas',
  route: 'ruta',
  routes: 'rutas',
  client: 'cliente',
  clients: 'clientes',
  period: 'periodo',
  periods: 'periodos',
};

/**
 * Extrae el campo duplicado de un mensaje de error de MySQL
 * @param {string} message - Mensaje de error
 * @returns {object} - { field: string, value: string, table: string }
 */
function parseDuplicateEntry(message) {
  // Patron: Duplicate entry 'valor' for key 'tabla_campo_unique'
  const duplicateMatch = message.match(/Duplicate entry '([^']+)' for key '([^']+)'/i);

  if (duplicateMatch) {
    const value = duplicateMatch[1];
    const keyName = duplicateMatch[2];

    // Extraer campo y tabla del nombre de la key
    // Formato comun: tabla_campo_unique o campo_unique
    const keyParts = keyName.split('_');

    let table = '';
    let field = '';

    // Buscar tabla conocida
    for (const [key, name] of Object.entries(tableNames)) {
      if (keyName.toLowerCase().includes(key)) {
        table = name;
        break;
      }
    }

    // Buscar campo conocido
    for (const [key, name] of Object.entries(fieldNames)) {
      if (keyName.toLowerCase().includes(key)) {
        field = name;
        break;
      }
    }

    // Si no encontramos el campo, usar el penultimo elemento
    if (!field && keyParts.length >= 2) {
      field = keyParts[keyParts.length - 2];
    }

    return { field, value, table };
  }

  return null;
}

/**
 * Parsea un error y retorna un mensaje amigable
 * @param {Error|object|string} error - Error a parsear
 * @returns {object} - { title: string, message: string, type: 'duplicate'|'validation'|'server'|'unknown' }
 */
export function parseError(error) {
  let message = '';

  // Extraer el mensaje del error
  if (typeof error === 'string') {
    message = error;
  } else if (error?.response?.data?.message) {
    message = error.response.data.message;
  } else if (error?.message) {
    message = error.message;
  } else if (error?.toString) {
    message = error.toString();
  }

  // Verificar si es un error de duplicado
  if (message.toLowerCase().includes('duplicate entry')) {
    const parsed = parseDuplicateEntry(message);

    if (parsed) {
      const fieldLabel = parsed.field || 'registro';
      const tableLabel = parsed.table ? ` de ${parsed.table}` : '';
      const color = typeColors.duplicate;

      return {
        title: 'Registro duplicado',
        html: `Ya existe una ${fieldLabel}${tableLabel} con el valor <span style="color: ${color}; font-weight: 600;">${parsed.value}</span>. Por favor, utilice un valor diferente.`,
        type: 'duplicate',
        details: parsed,
      };
    }

    return {
      title: 'Registro duplicado',
      message: 'El registro que intenta crear ya existe en el sistema. Por favor, verifique los datos e intente nuevamente.',
      type: 'duplicate',
    };
  }

  // Verificar errores de validacion
  if (message.toLowerCase().includes('formato de placa') || message.toLowerCase().includes('placa inválid') || message.toLowerCase().includes('placa invalid')) {
    return {
      title: 'Placa inválida',
      message: message,
      type: 'validation',
    };
  }

  // Verificar errores de validacion
  if (message.toLowerCase().includes('validation') || message.toLowerCase().includes('required')) {
    return {
      title: 'Error de validacion',
      message: message,
      type: 'validation',
    };
  }

  // Verificar errores de conexion
  if (message.toLowerCase().includes('network') || message.toLowerCase().includes('connection')) {
    return {
      title: 'Error de conexion',
      message: 'No se pudo conectar con el servidor. Por favor, verifique su conexion a internet e intente nuevamente.',
      type: 'network',
    };
  }

  // Verificar timeout
  if (message.toLowerCase().includes('timeout')) {
    return {
      title: 'Tiempo de espera agotado',
      message: 'La operacion tardo demasiado tiempo. Por favor, intente nuevamente.',
      type: 'timeout',
    };
  }

  // Verificar error de autenticacion
  if (message.toLowerCase().includes('unauthorized') || message.toLowerCase().includes('401')) {
    return {
      title: 'Sesion expirada',
      message: 'Su sesion ha expirado. Por favor, inicie sesion nuevamente.',
      type: 'auth',
    };
  }

  // Error generico del servidor
  if (message.includes('500') || message.toLowerCase().includes('internal server')) {
    return {
      title: 'Error del servidor',
      message: 'Ocurrio un error en el servidor. Por favor, intente nuevamente mas tarde.',
      type: 'server',
    };
  }

  // Si el mensaje es muy largo (probablemente SQL), dar mensaje generico
  if (message.length > 200) {
    return {
      title: 'Error',
      message: 'Ocurrio un error al procesar la solicitud. Por favor, contacte al administrador del sistema.',
      type: 'unknown',
      originalMessage: message,
    };
  }

  // Retornar mensaje original si no es muy tecnico
  return {
    title: 'Error',
    message: message || 'Ocurrio un error inesperado. Por favor, intente nuevamente.',
    type: 'unknown',
  };
}

/**
 * Funcion auxiliar especifica para errores de conductores
 * @param {Error|object|string} error - Error a parsear
 * @returns {object} - Mensaje amigable
 */
export function parseDriverError(error) {
  const parsed = parseError(error);
  const color = typeColors.duplicate;

  if (parsed.type === 'duplicate' && parsed.details) {
    const { field, value } = parsed.details;

    if (field === 'cédula' || field === 'cédula del conductor') {
      return {
        title: 'Conductor duplicado',
        html: `Ya existe un conductor registrado con la cédula <span style="color: ${color}; font-weight: 600;">${value}</span>. Por favor, verifique si el conductor ya está en el sistema.`,
        type: 'duplicate',
      };
    }

    if (field === 'número de placa') {
      return {
        title: 'Placa duplicada',
        html: `Ya existe un vehículo registrado con la placa <span style="color: ${color}; font-weight: 600;">${value}</span>.`,
        type: 'duplicate',
      };
    }
  }

  return parsed;
}

/**
 * Funcion auxiliar especifica para errores de clientes
 * @param {Error|object|string} error - Error a parsear
 * @returns {object} - Mensaje amigable
 */
export function parseClientError(error) {
  const parsed = parseError(error);
  const color = typeColors.duplicate;

  if (parsed.type === 'duplicate' && parsed.details) {
    const { field, value } = parsed.details;

    if (field === 'cédula' || field === 'cédula del cliente') {
      return {
        title: 'Cliente duplicado',
        html: `Ya existe un cliente registrado con la cédula <span style="color: ${color}; font-weight: 600;">${value}</span>. Por favor, verifique si el cliente ya está en el sistema.`,
        type: 'duplicate',
      };
    }
  }

  return parsed;
}

/**
 * Funcion auxiliar especifica para errores de periodos
 * @param {Error|object|string} error - Error a parsear
 * @returns {object} - Mensaje amigable
 */
export function parsePeriodError(error) {
  const parsed = parseError(error);
  const color = typeColors.duplicate;

  if (parsed.type === 'duplicate' && parsed.details) {
    const { field, value } = parsed.details;

    if (field === 'nombre' || field === 'nombre del periodo') {
      return {
        title: 'Periodo duplicado',
        html: `Ya existe un periodo con el nombre <span style="color: ${color}; font-weight: 600;">${value}</span>.`,
        type: 'duplicate',
      };
    }
  }

  return parsed;
}

const ErrorParser = {
  parseError,
  parseDriverError,
  parseClientError,
  parsePeriodError,
  typeColors,
};

export default ErrorParser;
